import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  MdClose, MdCheckCircle, MdAutoAwesome, MdReplay, MdTouchApp, MdArrowBack,
} from 'react-icons/md';
import { GUIDE_BY_ID, guidesFor } from './guides';
import Assistant from './Assistant';
import './coach.css';

/**
 * «ورّيني» — walks the user through a guide ON the real screen: the
 * element to use is lit, everything else is dimmed and does not take
 * clicks, and a bubble next to it says what to do. The step moves on by
 * itself when the user does it (a click, a save); typing steps move on
 * with «التالي» or Tab. Cancelling a popup or leaving the page steps back
 * to where the thread was lost (see the step fields in guides.js).
 *
 * Nothing here knows any page: steps find elements by `data-guide`, and
 * saves are heard through the `mtz:saved` event ui/api.js sends after
 * every successful write.
 */

const CoachContext = createContext({
  guides: [], done: [], activeId: null, start: () => {}, stop: () => {}, ask: () => {},
});
export const useCoach = () => useContext(CoachContext);

const doneKey = (uid) => `mtz.guides.${uid}`;
const readDone = (uid) => {
  try { return JSON.parse(localStorage.getItem(doneKey(uid)) || '[]'); } catch { return []; }
};

export function CoachProvider({
  user, role, nav, page, children,
}) {
  const [run, setRun] = useState(null);
  const [done, setDone] = useState(() => readDone(user?.id));
  const guides = useMemo(() => guidesFor(role, nav), [role, nav]);
  const uid = user?.id;

  const start = useCallback((id) => {
    if (GUIDE_BY_ID[id]) setRun({ id, key: `${id}:${Date.now()}` });
  }, []);
  const stop = useCallback(() => setRun(null), []);
  const markDone = useCallback((id) => {
    setDone((d) => {
      if (d.includes(id)) return d;
      const next = [...d, id];
      try { localStorage.setItem(doneKey(uid), JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [uid]);

  // «اسأل مشكاة»: open, optionally with a question already typed.
  const [asking, setAsking] = useState({ open: false, prefill: '' });
  const ask = useCallback((prefill = '') => setAsking({ open: true, prefill }), []);
  const closeAsk = useCallback(() => setAsking({ open: false, prefill: '' }), []);

  const value = useMemo(() => ({
    guides, nav, done, activeId: run?.id || null, start, stop, ask,
  }), [guides, nav, done, run, start, stop, ask]);

  return (
    <CoachContext.Provider value={value}>
      {children}
      {!run && guides.length > 0 && (
        <Assistant
          user={user}
          role={role}
          nav={nav}
          guides={guides}
          page={page}
          open={asking.open}
          prefill={asking.prefill}
          onOpen={() => ask()}
          onClose={closeAsk}
          onRun={start}
        />
      )}
      {run && (
        <CoachLayer
          key={run.key}
          guide={GUIDE_BY_ID[run.id]}
          page={page}
          onStop={stop}
          onDone={markDone}
          onStart={start}
        />
      )}
    </CoachContext.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Finding things on the screen                                         */
/* ------------------------------------------------------------------ */

const q = (sel) => document.querySelector(sel);
const PAD = 6;

/** The element's box when it is really on screen (not in a closed menu). */
function shownRect(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return null;
  if (r.right <= 0 || r.left >= window.innerWidth) return null;
  return r;
}

/** The first copy on screen (a page may keep hidden copies mounted). */
const firstShown = (sel) => [...document.querySelectorAll(sel)].find((el) => shownRect(el)) || null;

function resolve(step) {
  const list = Array.isArray(step.target) ? step.target : step.target ? [step.target] : [];
  for (const sel of list) {
    const el = firstShown(sel);
    if (el) return { el, via: false };
  }
  if (step.via) {
    const el = firstShown(step.via);
    if (el) return { el, via: true };
  }
  return null;
}

/** The lit area: the element plus an open dropdown hanging out of it. */
function holeOf(el) {
  const r = el.getBoundingClientRect();
  let top = r.top; let left = r.left; let right = r.right; let bottom = r.bottom;
  el.querySelectorAll('.ui-ssel-list, [data-guide-pop]').forEach((p) => {
    const pr = p.getBoundingClientRect();
    top = Math.min(top, pr.top); left = Math.min(left, pr.left);
    right = Math.max(right, pr.right); bottom = Math.max(bottom, pr.bottom);
  });
  return {
    top: Math.round(top - PAD),
    left: Math.round(left - PAD),
    width: Math.round(right - left + PAD * 2),
    height: Math.round(bottom - top + PAD * 2),
  };
}

const sameBox = (a, b) => a && b && a.via === b.via
  && Math.abs(a.top - b.top) < 1 && Math.abs(a.left - b.left) < 1
  && Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1;

function fieldIn(el) {
  if (el.matches('input,select,textarea')) return el;
  return el.querySelector('input:not([type=hidden]):not([type=checkbox]):not([disabled])')
    || el.querySelector('input[type=checkbox]')
    || el.querySelector('textarea, select');
}

/** Has the user given this field a value yet? */
function isFilled(el) {
  const combo = el.matches('.ui-ssel') ? el : el.querySelector('.ui-ssel');
  if (combo) return combo.classList.contains('has-value');
  // A multi-pick list: at least one ticked.
  if (el.querySelector('.ui-multipicker')) return !!el.querySelector('.ui-multipicker-chip, .ui-multipicker-row.is-checked');
  const f = fieldIn(el);
  if (!f) return false;
  if (f.type === 'checkbox') return f.checked;
  return String(f.value || '').trim() !== '';
}

/** Is the element cut off by a scrolling box around it (the sidebar list,
 *  a popup's body) or by the window? */
function isClipped(el) {
  const r = el.getBoundingClientRect();
  let top = Math.max(r.top, 56);
  let bottom = Math.min(r.bottom, window.innerHeight);
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const cs = getComputedStyle(p);
    if (cs.overflowY !== 'visible' && cs.overflowY !== 'clip') {
      const pr = p.getBoundingClientRect();
      top = Math.max(top, pr.top);
      bottom = Math.min(bottom, pr.bottom);
    }
  }
  // A long table only needs its start on screen.
  return bottom - top < Math.min(r.height - 2, window.innerHeight * 0.5);
}

/** Bring it into view in every box that scrolls (coach.css gives
 *  [data-guide] a scroll margin clear of the sticky top bar). */
function reveal(el, roomy) {
  const tall = el.getBoundingClientRect().height > window.innerHeight * 0.6;
  el.scrollIntoView({ block: tall ? 'start' : roomy ? 'center' : 'nearest', behavior: 'smooth' });
}

/** A field low on the screen: its dropdown (and the bubble) need room. */
const isLow = (el) => el.getBoundingClientRect().bottom > window.innerHeight * 0.72;

/* ------------------------------------------------------------------ */
/* Where the bubble goes                                                */
/* ------------------------------------------------------------------ */

function place(hole, bw, bh) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const M = 12;
  const G = 14;
  if (!hole) return { top: Math.max(M, (vh - bh) / 2), left: Math.max(M, (vw - bw) / 2), side: null };

  const clampX = (x) => Math.max(M, Math.min(vw - bw - M, x));
  const clampY = (y) => Math.max(M, Math.min(vh - bh - M, y));
  const cx = hole.left + hole.width / 2;
  const cy = hole.top + hole.height / 2;
  const bottom = hole.top + hole.height;

  if (vh - bottom - G >= bh + M) {
    const left = clampX(cx - bw / 2);
    return { top: bottom + G, left, side: 'top', arrow: cx - left };
  }
  if (hole.top - G >= bh + M) {
    const left = clampX(cx - bw / 2);
    return { top: hole.top - G - bh, left, side: 'bottom', arrow: cx - left };
  }
  if (hole.left - G >= bw + M) {
    const top = clampY(cy - bh / 2);
    return { top, left: hole.left - G - bw, side: 'right', arrow: cy - top };
  }
  if (vw - (hole.left + hole.width) - G >= bw + M) {
    const top = clampY(cy - bh / 2);
    return { top, left: hole.left + hole.width + G, side: 'left', arrow: cy - top };
  }
  // No room beside it (a long table, a phone): the bottom corner at the
  // start of the line, away from the row buttons at its end.
  return { top: vh - bh - M, left: vw - bw - M, side: null };
}

/* ------------------------------------------------------------------ */
/* The walkthrough                                                      */
/* ------------------------------------------------------------------ */

const LOST_AFTER = 4000;

function CoachLayer({
  guide, page, onStop, onDone, onStart,
}) {
  const { steps } = guide;
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState(null);
  const [lost, setLost] = useState(false);
  const [filled, setFilled] = useState(false);
  const [pos, setPos] = useState(null);
  // Reached the end through «أنشر لاحقاً»: the final card says so.
  const [later, setLater] = useState(false);

  const step = steps[index];
  const hasTarget = !!(step.target || step.via);
  const memo = useRef({});
  const bubbleRef = useRef(null);
  const holeRef = useRef(null);
  const live = useRef({});
  live.current = {
    index, step, spot, filled, page,
  };

  const go = useCallback((i) => {
    memo.current = {};
    setSpot(null);
    setLost(false);
    setFilled(false);
    setIndex(Math.max(0, Math.min(steps.length - 1, i)));
  }, [steps.length]);
  const goId = useCallback((id) => {
    const i = steps.findIndex((s) => s.id === id);
    if (i >= 0) go(i);
  }, [steps, go]);
  const next = useCallback(() => go(live.current.index + 1), [go]);

  // On arrival: a step that is already done (the page is open) is passed.
  useLayoutEffect(() => {
    if (step.skip && step.skip({ page: live.current.page, q })) go(index + 1);
  }, [index, step, go]);

  useEffect(() => {
    if (step.final) onDone(guide.id);
  }, [step, guide.id, onDone]);

  // Follow the target every frame: it moves when the page scrolls, a
  // popup opens, a table loads.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const s = steps[index];
      const m = memo.current;
      const now = performance.now();
      if (m.since == null) m.since = now;

      if (s.target || s.via) {
        const hit = resolve(s);
        if (hit) {
          m.missingSince = null;
          if (!hit.via) m.seen = true;
          // Once on arrival, and again whenever it ends up cut off
          // (never more than every 700 ms, while a smooth scroll runs).
          const roomy = s.wait === 'fill' && !hit.via;
          const arrived = !m.revealed || m.revealedVia !== hit.via;
          if ((isClipped(hit.el) || (arrived && roomy && isLow(hit.el)))
            && (!m.revealAt || now - m.revealAt > 700)) {
            m.revealAt = now;
            reveal(hit.el, roomy);
          }
          m.revealed = true;
          m.revealedVia = hit.via;
          // An open dropdown running off the bottom of the window.
          const pop = hit.el.querySelector('.ui-ssel-list, [data-guide-pop]');
          if (!pop) m.popShown = false;
          else if (!m.popShown && pop.getBoundingClientRect().bottom > window.innerHeight) {
            m.popShown = true;
            pop.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
          if (!hit.via && s.wait === 'fill' && !m.focused) {
            m.focused = true;
            const f = fieldIn(hit.el);
            if (f && document.activeElement !== f) f.focus({ preventScroll: true });
          }
          const box = { ...holeOf(hit.el), via: hit.via };
          setSpot((prev) => (sameBox(prev, box) ? prev : box));
          setFilled(s.wait === 'fill' && !hit.via ? isFilled(hit.el) : false);
                setLost(false);
        } else {
          if (m.missingSince == null) m.missingSince = now;
          const gone = now - m.missingSince;
          // The popup was cancelled or the page left: back to the step
          // that leads here again.
          if (s.back && ((m.seen && gone > 500) || (!m.seen && gone > 3000))) {
            goId(s.back);
            return;
          }
          setSpot(null);
          if (gone > LOST_AFTER) setLost(true);
        }
      }

      if (s.advanceWhen && s.advanceWhen({ page: live.current.page, q })) {
        go(index + 1);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index, steps, go, goId]);

  // A save anywhere ahead finishes that step, even when it came from
  // Enter in the form rather than from the lit «حفظ».
  useEffect(() => {
    const onSaved = (e) => {
      const { method, path } = e.detail || {};
      const bare = String(path || '').split('?')[0];
      const from = live.current.index;
      const i = steps.findIndex((s, k) => k >= from && s.expect
        && s.expect[0] === method && s.expect[1].test(bare));
      if (i >= 0) go(i + 1);
    };
    window.addEventListener('mtz:saved', onSaved);
    return () => window.removeEventListener('mtz:saved', onSaved);
  }, [steps, go]);

  // Clicks: only the lit area and the bubble take them (a `free` step
  // leaves the whole screen usable). A click that lands on what the
  // step asks for moves on.
  useEffect(() => {
    const inHole = (x, y) => {
      const h = live.current.spot;
      return h && x >= h.left && x <= h.left + h.width && y >= h.top && y <= h.top + h.height;
    };
    const shake = () => {
      [bubbleRef.current, holeRef.current].forEach((el) => {
        if (!el) return;
        el.classList.remove('is-nudge');
        void el.offsetWidth; // restart the animation
        el.classList.add('is-nudge');
      });
    };
    const guard = (e) => {
      if (bubbleRef.current?.contains(e.target)) return;
      const s = live.current.step;
      const h = live.current.spot;
      // Looking for a target that is not there: let the user act.
      if ((s.target || s.via) && !h) return;
      const pt = e.touches ? e.touches[0] : e;
      const inside = h && pt && inHole(pt.clientX, pt.clientY);
      if (inside && e.type === 'click' && !h.via && !s.wait && !s.expect && !s.advanceWhen) {
        const sel = s.clickOn || [].concat(s.target).join(',');
        if (e.target.closest && e.target.closest(sel)) setTimeout(next, 0);
      }
      // Keyboard activation (Enter / Space on a focused button).
      if (inside || s.free || (e.type === 'click' && e.detail === 0)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'pointerdown' || e.type === 'touchstart') shake();
    };
    const types = ['pointerdown', 'mousedown', 'mouseup', 'click', 'dblclick', 'touchstart'];
    types.forEach((t) => window.addEventListener(t, guard, { capture: true, passive: false }));
    return () => types.forEach((t) => window.removeEventListener(t, guard, { capture: true }));
  }, [next]);

  // Esc ends the guide (and only that — not the popup under it).
  // Tab out of a filled field = «التالي».
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onStop();
        return;
      }
      const { step: s, filled: f } = live.current;
      if (e.key === 'Tab' && !e.shiftKey && s.wait === 'fill' && f) {
        const el = [].concat(s.target).map(firstShown).find(Boolean);
        if (el && el.contains(document.activeElement)) setTimeout(next, 0);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onStop, next]);

  const lit = hasTarget && spot;
  const floating = hasTarget && !spot;

  useLayoutEffect(() => {
    const b = bubbleRef.current;
    if (!b) return;
    const measure = () => {
      const p = place(lit ? spot : null, b.offsetWidth, b.offsetHeight);
      if (floating) {
        p.top = window.innerHeight - b.offsetHeight - 16;
        p.left = Math.max(12, (window.innerWidth - b.offsetWidth) / 2);
        p.side = null;
      }
      setPos((prev) => (prev && Math.abs(prev.top - p.top) < 1 && Math.abs(prev.left - p.left) < 1
        && prev.side === p.side && Math.abs((prev.arrow || 0) - (p.arrow || 0)) < 1 ? prev : p));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [spot, lit, floating, index, lost, filled]);

  const total = steps.filter((s) => !s.final).length;
  const shown = Math.min(index + 1, total);
  const nextGuide = step.next ? GUIDE_BY_ID[step.next] : null;

  let actions = null;
  if (step.final) {
    actions = (
      <>
        <button type="button" className="coach-btn is-primary" onClick={onStop}>إنهاء</button>
        {step.again && (
          <button type="button" className="coach-btn" onClick={() => goId(step.again.to)}>
            <MdReplay size={16} /> {step.again.label}
          </button>
        )}
        {nextGuide && (
          <button type="button" className="coach-btn" onClick={() => onStart(nextGuide.id)}>
            التالي: {nextGuide.title} <MdArrowBack size={15} />
          </button>
        )}
      </>
    );
  } else if (lost) {
    actions = (
      <>
        <button type="button" className="coach-btn is-primary" onClick={() => go(0)}>
          <MdReplay size={16} /> ابدأ الدليل من جديد
        </button>
        <button type="button" className="coach-btn" onClick={onStop}>إنهاء</button>
      </>
    );
  } else if (!hasTarget || step.wait === 'next') {
    actions = (
      <button type="button" className="coach-btn is-primary" onClick={next}>
        {step.nextLabel || 'التالي'} <MdArrowBack size={15} />
      </button>
    );
  } else if (step.wait === 'fill' && spot && !spot.via) {
    actions = (
      <>
        <button type="button" className="coach-btn is-primary" onClick={next} disabled={!filled && !step.optional}>
          {!filled && step.optional ? 'تخطَّ' : 'التالي'} <MdArrowBack size={15} />
        </button>
        <span className="coach-hint">{filled ? 'أو اضغط Tab' : ''}</span>
      </>
    );
  } else if (spot) {
    actions = (
      <span className="coach-hint"><MdTouchApp size={16} /> اضغط على المكان المضيء</span>
    );
  }
  // «أنشر لاحقاً»: a step the user may leave for another day.
  if (step.skipLabel && !step.final && !lost) {
    actions = (
      <>
        {actions}
        <button type="button" className="coach-btn" onClick={() => { setLater(true); if (step.skipTo) goId(step.skipTo); else go(steps.length - 1); }}>
          {step.skipLabel}
        </button>
      </>
    );
  }

  let kind = 'click';
  if (step.final) kind = 'final';
  else if (lost) kind = 'lost';
  else if (!hasTarget || step.wait === 'next') kind = 'next';
  else if (!spot) kind = 'wait';
  else if (spot.via) kind = 'via';
  else if (step.wait === 'fill') kind = 'fill';

  let text = step.final && later && step.laterText ? step.laterText : step.text;
  const title = step.final && later && step.laterTitle ? step.laterTitle : step.title;
  if (spot?.via) text = step.viaText || text;
  if (lost) text = 'لا أجد هذا في الشاشة الآن. ربما أُغلقت نافذة أو تغيّرت الصفحة.';

  return createPortal(
    <div className="coach" dir="rtl">
      {lit ? (
        <div
          ref={holeRef}
          className={`coach-hole${spot.via ? ' is-via' : ''}${step.free ? ' is-free' : ''}`}
          style={{
            top: spot.top, left: spot.left, width: spot.width, height: spot.height,
          }}
        />
      ) : !hasTarget ? <div className="coach-dim" /> : null}

      <div
        ref={bubbleRef}
        key={`b${index}`}
        className={`coach-bubble${step.final ? ' is-final' : ''}`}
        style={{
          top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden',
        }}
        role="dialog"
        aria-label={guide.title}
        // For guide-check.cjs: where the walk is.
        data-guide-id={guide.id}
        data-step={index}
        data-kind={kind}
        data-target={[].concat(step.target || []).join('|')}
        data-click={step.clickOn || ''}
      >
        {lit && pos?.side && (
          <span
            className={`coach-arrow is-${pos.side}`}
            style={pos.side === 'top' || pos.side === 'bottom'
              ? { left: Math.max(18, Math.min((bubbleRef.current?.offsetWidth || 320) - 18, pos.arrow)) }
              : { top: Math.max(18, Math.min((bubbleRef.current?.offsetHeight || 160) - 18, pos.arrow)) }}
          />
        )}
        <div className="coach-head">
          <span className="coach-kicker"><MdAutoAwesome size={14} /> {guide.title}</span>
          <button type="button" className="coach-close" onClick={onStop} aria-label="إنهاء الدليل" title="إنهاء الدليل (Esc)">
            <MdClose size={18} />
          </button>
        </div>
        {!step.final && (
          <div className="coach-progress" aria-hidden="true">
            <span style={{ width: `${(shown / total) * 100}%` }} />
          </div>
        )}
        <div className="coach-body">
          {step.final && <span className="coach-done-icon"><MdCheckCircle size={40} /></span>}
          <div>
            {!step.final && <div className="coach-count">الخطوة {shown} من {total}</div>}
            <h3 className="coach-title">{title}</h3>
            <p className="coach-text">{text}</p>
            {floating && !lost && <p className="coach-wait"><span className="coach-spin" /> بانتظار ظهوره على الشاشة…</p>}
          </div>
        </div>
        {actions && <div className="coach-actions">{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}
